import type { ReactNode } from 'react'

import styles from './Charts.module.css'
import { CHART, PLOT_BOTTOM, PLOT_RIGHT, yOf, type Span, xOf } from './geometry.js'

export interface FrameTick {
  readonly quantity: number
  readonly label: string
}

export interface SideAxis {
  readonly range: { readonly min: number; readonly max: number }
  readonly ticks: readonly FrameTick[]
  /** Which series this axis belongs to, when there is more than one. */
  readonly colour?: string
}

interface ChartFrameProps {
  /** Announced to a screen reader in place of the drawing. */
  readonly label: string
  readonly valueRange: { readonly min: number; readonly max: number }
  readonly valueTicks: readonly FrameTick[]
  /**
   * A second axis on the right.
   *
   * Only ever offered for two tracks (T-404): with three, a reader has no way
   * to tell which line belongs to which axis, and a chart nobody can attribute
   * is worse than one that admits it has no shared scale.
   */
  readonly rightAxis?: SideAxis
  readonly leftColour?: string
  readonly span: Span
  readonly timeTicks: readonly { readonly epochMs: number; readonly label: string }[]
  /**
   * Painted behind the gridlines rather than over them.
   *
   * The area wash under a line has to sit under the scaffolding, or the dashes
   * show through it and the whole point of softening them is lost.
   */
  readonly underlay?: ReactNode
  readonly children: ReactNode
}

/**
 * Axes, gridlines and the box the data is drawn in.
 *
 * `role="img"` with a label rather than an accessible SVG tree: the shapes carry
 * no information a screen reader could use, and the values are published
 * properly as a table beside the chart (T-403). Announcing four hundred `<path>`
 * elements would be worse than announcing none.
 */
export function ChartFrame({
  label,
  valueRange,
  valueTicks,
  rightAxis,
  leftColour,
  span,
  timeTicks,
  underlay,
  children,
}: ChartFrameProps) {
  return (
    <svg
      className={styles['chart']}
      // Widened rather than re-laid-out when a second axis appears, so the plot
      // geometry — and therefore every line on it — is identical either way.
      viewBox={`0 0 ${String(CHART.width + (rightAxis === undefined ? 0 : 34))} ${String(CHART.height)}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
    >
      {/*
        A surface for the data to sit on, so it is not floating on bare paper.
        Rounded, and the first thing drawn.
      */}
      <rect
        x={CHART.padLeft}
        y={CHART.padTop}
        width={PLOT_RIGHT - CHART.padLeft}
        height={PLOT_BOTTOM - CHART.padTop}
        rx={10}
        className={styles['plotBand']}
      />

      {underlay}

      {valueTicks.map((tick) => {
        const y = yOf(tick.quantity, valueRange)
        return (
          <g key={`${String(tick.quantity)}-${tick.label}`}>
            <line
              x1={CHART.padLeft}
              y1={y}
              x2={PLOT_RIGHT}
              y2={y}
              className={styles['grid']}
            />
            <text
              x={CHART.padLeft - 5}
              y={y}
              className={styles['axisLabel']}
              textAnchor="end"
              dominantBaseline="middle"
              fill={leftColour}
            >
              {tick.label}
            </text>
          </g>
        )
      })}

      {rightAxis?.ticks.map((tick) => (
        <text
          key={`right-${String(tick.quantity)}-${tick.label}`}
          x={PLOT_RIGHT + 4}
          y={yOf(tick.quantity, rightAxis.range)}
          className={styles['axisLabel']}
          textAnchor="start"
          dominantBaseline="middle"
          fill={rightAxis.colour}
        >
          {tick.label}
        </text>
      ))}

      <line
        x1={CHART.padLeft}
        y1={PLOT_BOTTOM}
        x2={PLOT_RIGHT}
        y2={PLOT_BOTTOM}
        className={styles['axis']}
      />

      {timeTicks.map((tick) => (
        <text
          key={tick.epochMs}
          x={xOf(tick.epochMs, span)}
          y={PLOT_BOTTOM + 14}
          className={styles['axisLabel']}
          textAnchor="middle"
        >
          {tick.label}
        </text>
      ))}

      {children}
    </svg>
  )
}
